import React, {
  useEffect,
  useState
} from "react";

import {
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  migrateLegacyItemIds
} from "../../utils/migrateLegacyItemIds";

import {
  getInventoryLedger
} from "../../services/inventoryLedgerService";

export default function InventoryDashboard({
  companyId
}) {

  const [
    summary,
    setSummary
  ] = useState([]);

  const [
    ledger,
    setLedger
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const LOW_STOCK_THRESHOLD = 10;

  const [
    stats,
    setStats
  ] = useState({

    totalItems: 0,

    totalQty: 0,

    lowStock: 0,

    defectiveStock: 0,

    negativeStockItems: 0,
  });

  /* =================================================
     FETCH DASHBOARD
  ================================================= */

  const fetchDashboard =
    async () => {

      setLoading(true);

      try {

        if (!companyId) {

          throw new Error(
            "companyId required"
          );
        }

        // =========================================
        // SUMMARY FETCH
        // cache only
        // =========================================

        const summaryQuery =
          query(

            collection(
              db,
              "inventory_summary"
            ),

            where(
              "company_id",
              "==",
              companyId
            )
          );

        const inventorySnap =
          await getDocs(
            summaryQuery
          );

        const inventoryData =
          inventorySnap.docs.map(
            (docSnap) => ({

              id:
                docSnap.id,

              ...docSnap.data(),

              qty:
                Number(
                  docSnap.data()?.qty || 0
                ),

              defective_stock:
                Number(
                  docSnap.data()?.defective_stock || 0
                ),
            })
          );

        // =========================================
        // FILTER ACTIVE ITEMS
        // =========================================

        const activeInventory =
          inventoryData.filter(
            (item) =>
              item.status !== "disabled"
          );

        // =========================================
        // STATS
        // =========================================

        const totalItems =
          activeInventory.length;

        const totalQty =
          activeInventory.reduce(

            (sum, item) =>

              sum +
              Number(
                item.qty || 0
              ),

            0
          );

        const lowStock =
          activeInventory.filter(

            (item) =>

              Number(
                item.qty || 0
              ) < LOW_STOCK_THRESHOLD
          ).length;

        const defectiveStock =
          activeInventory.reduce(

            (sum, item) =>

              sum +
              Number(
                item.defective_stock || 0
              ),

            0
          );

        // =========================================
        // NEGATIVE STOCK AUDIT
        // =========================================

        const negativeStockItems =
          activeInventory.filter(

            (item) =>

              Number(
                item.qty || 0
              ) < 0
          ).length;

        if (
          negativeStockItems > 0
        ) {

          console.error(
            "NEGATIVE STOCK DETECTED"
          );
        }

        // =========================================
        // SAVE SUMMARY
        // =========================================

        setSummary(
          activeInventory
        );

        setStats({

          totalItems,

          totalQty,

          lowStock,

          defectiveStock,

          negativeStockItems,
        });

        // =========================================
        // REPLAY-SAFE LEDGER
        // =========================================

        const replayLedger =
          await getInventoryLedger(
            companyId
          );

        setLedger(
          replayLedger.slice(0, 10)
        );

      } catch (error) {

        console.error(
          "Dashboard Fetch Error:",
          error
        );

      } finally {

        setLoading(false);
      }
    };

  /* =================================================
     INITIAL LOAD
  ================================================= */

  useEffect(() => {

    fetchDashboard();

  }, []);

  /* =================================================
     LOW STOCK
  ================================================= */

  const lowStockItems =
    summary.filter(

      (item) =>

        Number(
          item.qty || 0
        ) < LOW_STOCK_THRESHOLD
    );

  /* =================================================
     UI
  ================================================= */

  return (

    <div className="min-h-screen bg-gray-100 p-6">

      <div className="max-w-7xl mx-auto">

        {/* ===================================== */}
        {/* HEADER */}
        {/* ===================================== */}

        <h1 className="text-3xl font-bold mb-6">
          Inventory Dashboard
        </h1>

        {/* ===================================== */}
        {/* MIGRATION */}
        {/* ===================================== */}

        <button

          onClick={async () => {

            const confirmRun =
              window.confirm(
                "Run legacy item_id migration?"
              );

            if (!confirmRun) {
              return;
            }

            try {

              await migrateLegacyItemIds();

              alert(
                "Migration completed successfully"
              );

              fetchDashboard();

            } catch (error) {

              console.error(
                error
              );

              alert(
                "Migration failed"
              );
            }
          }}

          className="
            mb-6
            px-6
            py-3
            rounded-xl
            bg-blue-600
            hover:bg-blue-700
            text-white
            font-semibold
            shadow
          "
        >
          RUN LEGACY ITEM MIGRATION
        </button>

        {/* ===================================== */}
        {/* LOADING */}
        {/* ===================================== */}

        {loading ? (

          <div className="text-lg">
            Loading dashboard...
          </div>

        ) : (

          <>

            {/* ===================================== */}
            {/* STATS */}
            {/* ===================================== */}

            <div className="
              grid
              grid-cols-1
              md:grid-cols-5
              gap-6
              mb-8
            ">

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Total Items
                </h2>

                <p className="text-3xl font-bold">
                  {stats.totalItems}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Total Stock Qty
                </h2>

                <p className="text-3xl font-bold">
                  {stats.totalQty}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Low Stock Items
                </h2>

                <p className="text-3xl font-bold text-yellow-500">
                  {stats.lowStock}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Defective Stock
                </h2>

                <p className="text-3xl font-bold text-red-500">
                  {stats.defectiveStock}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Negative Stock
                </h2>

                <p className="text-3xl font-bold text-red-600">
                  {stats.negativeStockItems}
                </p>
              </div>
            </div>

            {/* ===================================== */}
            {/* LOW STOCK ALERTS */}
            {/* ===================================== */}

            <div className="
              bg-white
              rounded-xl
              shadow
              p-6
              mb-8
            ">

              <h2 className="text-2xl font-bold mb-4">
                Low Stock Alerts
              </h2>

              <div className="overflow-x-auto">

                <table className="w-full border-collapse">

                  <thead>

                    <tr className="bg-gray-800 text-white">

                      <th className="p-3 text-left">
                        Item
                      </th>

                      <th className="p-3 text-left">
                        Code
                      </th>

                      <th className="p-3 text-left">
                        Category
                      </th>

                      <th className="p-3 text-left">
                        Qty
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {lowStockItems.length === 0 ? (

                      <tr>

                        <td
                          colSpan="4"
                          className="
                            p-4
                            text-center
                            text-gray-500
                          "
                        >
                          No low stock alerts
                        </td>
                      </tr>

                    ) : (

                      lowStockItems.map((item) => (

                        <tr
                          key={item.id}
                          className="border-b"
                        >

                          <td className="p-3">
                            {item.item_name || "-"}
                          </td>

                          <td className="p-3">
                            {item.item_code || "-"}
                          </td>

                          <td className="p-3">
                            {item.category || "-"}
                          </td>

                          <td className="
                            p-3
                            text-red-500
                            font-bold
                          ">
                            {item.qty || 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ===================================== */}
            {/* RECENT INVENTORY ACTIVITY */}
            {/* ===================================== */}

            <div className="
              bg-white
              rounded-xl
              shadow
              p-6
            ">

              <h2 className="text-2xl font-bold mb-4">
                Recent Inventory Activity
              </h2>

              <div className="overflow-x-auto">

                <table className="w-full border-collapse">

                  <thead>

                    <tr className="bg-gray-800 text-white">

                      <th className="p-3 text-left">
                        Date
                      </th>

                      <th className="p-3 text-left">
                        Item
                      </th>

                      <th className="p-3 text-left">
                        Movement
                      </th>

                      <th className="p-3 text-left">
                        Effect
                      </th>

                      <th className="p-3 text-left">
                        Balance
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {ledger.length === 0 ? (

                      <tr>

                        <td
                          colSpan="5"
                          className="
                            p-4
                            text-center
                            text-gray-500
                          "
                        >
                          No recent inventory activity
                        </td>
                      </tr>

                    ) : (

                      ledger.map((entry) => (

                        <tr
                          key={entry.id}
                          className="border-b"
                        >

                          <td className="p-3">

                            {entry.created_at?.seconds

                              ? new Date(

                                  entry.created_at.seconds *
                                  1000

                                ).toLocaleString()

                              : "-"}
                          </td>

                          <td className="p-3">
                            {entry.item_name || "-"}
                          </td>

                          <td className="p-3">
                            {entry.movement_type || "-"}
                          </td>

                          <td className="p-3">

                            {entry.stock_effect || 0}
                          </td>

                          <td className="p-3 font-semibold">

                            {entry.stock_after || 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}